/**
 * The thinking habit in games (docs/PATH-V2.md §7.4, §3.6 p.24, §9.8) — stages 1–2 with the path on, a parameter of
 * the lesson director like «тихий учитель» (./quiet.ts):
 *
 *  - 👀 «Что он хочет?»: the bot's moved piece glows for `HABIT_GLOW_MS` with yellow «достаёт» lines to the child's
 *    pieces it reaches (StageSign 'glow' + 'reach'); the glow fades game by game (`habitGlowStrength` of the habit games
 *    played before — stored per child by the caller) and goes out after about two dozen games. Now and then (fewer
 *    and fewer, `habitWantMax`) the line `v3.path.habit.want` is said with it;
 *  - 🚦 «Безопасно?»: about every 3rd child move at first, then rarer (`habitAskEvery`) — a question the child answers by
 *    moving. The icon is the cue (§3.1 p.1: an icon and 1–3 words): the line `v3.path.habit.safe` is said only the
 *    first `habitSafeSayMax` times a game, later the 🚦 comes alone;
 *  - the process is praised (`v3.path.habit.praise`, «Думаешь — и ход безопасный!») when a tempting losing move was
 *    there (`temptingMoves`: a capture that is no gift and loses material or lets the opponent mate) and the child
 *    thought and played a safe one ALONE: never a move made with a hint, an arrow or a quiet reveal (`helped`), never a
 *    move the judgement shows losing (`lossPct` ≥ `HABIT_PRAISE_MAX_LOSS`);
 *  - at most one «не спеши» a game (`v3.path.habit.slow`): a hasty (or asked) move that gives a piece ≥ 3 away.
 * In a «тихий учитель» game the habit's lines count against its 15 lines (`HabitSetup.lines`, §7.1).
 *
 * «Безопасно» is the one definition of analysis/gifts.ts (`isSafeMove`) plus «no losing capture» (static exchange)
 * plus «no mate in one for the opponent» (path/own.ts `allowsMateInOne`).
 * The habit lines are no help with the move (no arrow, no hint): they are journalled without `help`, a move after them
 * stays the child's own (§3.10). `voice: false` (a «Учитель» game — the teacher talks every move): the glow only.
 *
 * Pure: the planner only looks and returns the next memory; the caller says the event ('pathLesson': it passes the
 * parent's «Тихо») and keeps the memory (the resume snapshot). Timers stay with the caller (apps/web gameStore.ts).
 */
import { Chess } from 'chess.js';
import type { CoachEvent, Color, MascotPose, Square, StageSign } from '@gambit/shared';
import { HABIT_POOL, PATH_HABIT_LINES } from '@gambit/content';
import { parsePlacement, seeCapture, squareIndex } from '../../analysis/board.ts';
import { isGift, isSafeMove, moveGiftFacts, reaches } from '../../analysis/gifts.ts';
import { allowsMateInOne } from '../../path/own.ts';
import type { Rng } from '../phrase.ts';

/** How long the bot's piece glows (§7.4: «примерно 1,5 секунды»). */
export const HABIT_GLOW_MS = 1_500;
/** Below this the glow is not drawn at all (the habit has taken over). */
export const HABIT_GLOW_MIN = 0.15;
/** «Думаешь — и ход безопасный!» at most this often a game. */
export const HABIT_PRAISE_MAX = 2;
/** A move faster than this is «hasty» (and praise for thinking needs at least `HABIT_THINK_MS`). */
export const HABIT_HASTY_MS = 4_000;
export const HABIT_THINK_MS = 2_000;
/** A capture losing at least this much (centipawns, static exchange) is a losing one. */
const LOSING_CAPTURE_CP = -150;
/** «не спеши» is about a piece (knight or more, like `ownHung`): a capture losing this much gives one away. */
const GAVE_PIECE_CP = -200;
/** 👀 lines are at least this many plies apart. */
const WANT_GAP_PLIES = 6;
/** A judged move losing at least this much (win% points) is never praised as «безопасный», whatever the facts say. */
export const HABIT_PRAISE_MAX_LOSS = 10;

/** How the habit runs in this game. */
export interface HabitSetup {
  /** habit games the child played before this one (the fade; stored per child by the caller) */
  games: number;
  /** false: glow only («Учитель» talks every move himself) */
  voice: boolean;
  /**
   * lines the game may still say (a «тихий учитель» game: its budget left, core quiet.ts `quietBudgetLeft` — the
   * caller commits a said habit line there); absent = no cap. 0: the icons and the glow only.
   */
  lines?: number;
}

export interface HabitMemory {
  v: 1;
  /** child turns seen this game */
  turns: number;
  /** the turn of the last 🚦 question (0 = none yet) */
  lastAsk: number;
  asks: number;
  /** 🚦 questions said aloud this game (the rest came as the icon alone) */
  safeSaid: number;
  praises: number;
  slowSaid: boolean;
  wants: number;
  /** ply of the last 👀 line */
  lastWant: number | null;
  /** pool → the wording said last (not twice in a row) */
  said: Record<string, number>;
}

export function initialHabitMemory(): HabitMemory {
  return { v: 1, turns: 0, lastAsk: 0, asks: 0, safeSaid: 0, praises: 0, slowSaid: false, wants: 0, lastWant: null, said: {} };
}

const count = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.floor(v) : 0);

/** Tolerant reader (resume snapshot). */
export function restoreHabitMemory(value: unknown): HabitMemory {
  if (typeof value !== 'object' || value === null) return initialHabitMemory();
  const v = value as Record<string, unknown>;
  const said: Record<string, number> = {};
  if (typeof v.said === 'object' && v.said !== null) for (const [k, n] of Object.entries(v.said as Record<string, unknown>)) if (count(n) > 0) said[k] = count(n);
  return {
    v: 1,
    turns: count(v.turns),
    lastAsk: count(v.lastAsk),
    asks: count(v.asks),
    safeSaid: count(v.safeSaid),
    praises: count(v.praises),
    slowSaid: v.slowSaid === true,
    wants: count(v.wants),
    lastWant: typeof v.lastWant === 'number' && Number.isFinite(v.lastWant) ? v.lastWant : null,
    said,
  };
}

// ───────────────────────── the fade (per child, game by game) ─────────────────────────

/** The glow of the bot's piece in the child's next game: 1 in the first games, then fading; 0 = off. */
export function habitGlowStrength(games: number): number {
  const s = 1 / (1 + Math.max(0, games) / 4);
  return s < HABIT_GLOW_MIN ? 0 : Math.round(s * 100) / 100;
}

/** 🚦 «Безопасно?» every this many child moves: 3 at first, then rarer. */
export function habitAskEvery(games: number): number {
  return games < 5 ? 3 : games < 10 ? 4 : games < 20 ? 5 : 6;
}

/** 👀 «Что он хочет?» lines a game (the glow speaks for itself later). */
export function habitWantMax(games: number): number {
  return games < 5 ? 2 : games < 12 ? 1 : 0;
}

/** 🚦 «Безопасно?» said aloud at most this many times a game (later the icon alone is the cue). */
export function habitSafeSayMax(games: number): number {
  return games < 10 ? 2 : 1;
}

/** May the game still say a habit line («тихий учитель»: its budget of lines)? */
function canSay(setup: HabitSetup): boolean {
  return setup.voice && (setup.lines === undefined || setup.lines > 0);
}

// ───────────────────────── facts ─────────────────────────

function turnOf(fen: string): Color {
  return fen.trim().split(/\s+/)[1] === 'b' ? 'b' : 'w';
}

function legalUcis(fen: string): string[] {
  try {
    return new Chess(fen).moves({ verbose: true }).map((m) => `${m.from}${m.to}${m.promotion ?? ''}`);
  } catch {
    return [];
  }
}

function inCheck(fen: string): boolean {
  try {
    return new Chess(fen).inCheck();
  } catch {
    return false;
  }
}

/** After the move the opponent can mate in one (a «safe» capture or quiet move that walks into a mate is not safe). */
function letsMate(fen: string, uci: string): boolean {
  try {
    return allowsMateInOne(fen, uci);
  } catch {
    return false;
  }
}

/** Static exchange of the capture `uci` (centipawns, mover's view); 0 for a quiet move. */
function captureSee(fen: string, uci: string): number {
  const board = parsePlacement(fen);
  const from = squareIndex(uci.slice(0, 2));
  const to = squareIndex(uci.slice(2, 4));
  if (from < 0 || to < 0 || !board[to]) return 0;
  return seeCapture(board, from, to);
}

/**
 * The tempting losing moves of the side to move: captures that are no gift (`isGift`) and lose material — a guarded
 * piece taken by a dearer one («мы тоже отдаём»), a «poisoned» capture, a capture after which the opponent mates in
 * one. Sorted as generated.
 */
export function temptingMoves(fen: string): string[] {
  const board = parsePlacement(fen);
  const out: string[] = [];
  for (const uci of legalUcis(fen)) {
    const to = squareIndex(uci.slice(2, 4));
    if (to < 0 || !board[to]) continue;
    let gift = false;
    try {
      gift = isGift(fen, uci);
    } catch {
      gift = false;
    }
    if (gift) continue;
    if (captureSee(fen, uci) <= LOSING_CAPTURE_CP || !safeMove(fen, uci) || letsMate(fen, uci)) out.push(uci);
  }
  return out;
}

function safeMove(fen: string, uci: string): boolean {
  try {
    return isSafeMove(fen, uci);
  } catch {
    return false;
  }
}

/** Is the child's move safe in the habit's sense: nothing new left as a gift, no losing capture, no mate in one for him. */
export function habitSafe(fen: string, uci: string): boolean {
  return safeMove(fen, uci) && captureSee(fen, uci) > LOSING_CAPTURE_CP && !letsMate(fen, uci);
}

/** Did the move give a piece ≥ 3 away (left as a gift, or lost in a losing capture of one)? */
function gaveAPiece(fen: string, uci: string): boolean {
  let hung = false;
  try {
    hung = moveGiftFacts(fen, uci).ownHung;
  } catch {
    hung = false;
  }
  return hung || captureSee(fen, uci) <= GAVE_PIECE_CP;
}

// ───────────────────────── words ─────────────────────────

/** A habit line as a 'pathLesson' event with its recorded wording (never the one said last in this pool). */
function line(id: string, pool: string, pose: MascotPose, memory: HabitMemory, rng: Rng): { event: CoachEvent; memory: HabitMemory } | null {
  const all = (PATH_HABIT_LINES[pool] ?? []).map((w, i) => ({ t: w.t, n: i + 1 }));
  if (all.length === 0) return null;
  const last = memory.said[pool];
  const choice = all.length > 1 ? all.filter((w) => w.n !== last) : all;
  const w = choice[Math.min(choice.length - 1, Math.floor(rng() * choice.length))] ?? all[0];
  if (!w) return null;
  const event: CoachEvent = { id, kind: 'pathLesson', priority: 1, text: w.t, bubbleText: w.t, pose, pauseClock: true, say: [{ pool, n: w.n }] };
  return { event, memory: { ...memory, said: { ...memory.said, [pool]: w.n } } };
}

// ───────────────────────── the bot's move: 👀 ─────────────────────────

export interface HabitBotArgs {
  /** the position after the bot's move (the child to move) */
  fenAfter: string;
  /** the bot's move (UCI) */
  uci: string;
  childColor: Color;
  ply: number;
}

export interface HabitBotMoment {
  /** the glow of the moved piece and its «достаёт» lines to the child's pieces (empty when the glow is off) */
  signs: StageSign[];
  /** how long the signs stay */
  ms: number;
  /** squares of the child's pieces the moved piece reaches («что он хочет») */
  targets: Square[];
  /** 👀 said with it (null: the glow alone) */
  event: CoachEvent | null;
}

/** 👀 after the bot's move: the glow + lines, now and then the line. Null when the glow is off and nothing is said. */
export function planHabitBotMove(args: HabitBotArgs, setup: HabitSetup, memory: HabitMemory, rng: Rng = Math.random): { moment: HabitBotMoment | null; memory: HabitMemory } {
  const to = args.uci.slice(2, 4);
  const strength = habitGlowStrength(setup.games);
  let targets: Square[] = [];
  try {
    const board = parsePlacement(args.fenAfter);
    targets = reaches(args.fenAfter, to, { targets: true }).filter((sq) => {
      const p = board[squareIndex(sq)];
      return !!p && p.color === args.childColor && p.type !== 'k';
    });
  } catch {
    targets = [];
  }
  const signs: StageSign[] = strength > 0 ? [{ kind: 'glow', square: to, strength }, ...targets.map((sq): StageSign => ({ kind: 'reach', from: to, to: sq }))] : [];
  let next = memory;
  let event: CoachEvent | null = null;
  const room = memory.wants < habitWantMax(setup.games) && (memory.lastWant === null || args.ply - memory.lastWant >= WANT_GAP_PLIES);
  if (canSay(setup) && strength > 0 && targets.length > 0 && room) {
    const said = line(`habit-want-${args.ply}`, HABIT_POOL.want, 'point', memory, rng);
    if (said) {
      event = said.event;
      next = { ...said.memory, wants: memory.wants + 1, lastWant: args.ply };
    }
  }
  if (signs.length === 0 && event === null) return { moment: null, memory: next };
  return { moment: { signs, ms: HABIT_GLOW_MS, targets, event }, memory: next };
}

// ───────────────────────── the child's turn: 🚦 ─────────────────────────

export interface HabitTurnArgs {
  /** the child to move */
  fen: string;
  childColor: Color;
  /** the child's ply about to be played */
  ply: number;
}

export interface HabitAsk {
  /** said with the 🚦 (null when the game has no voice for the habit) */
  event: CoachEvent | null;
  /** the tempting losing moves of this position (the praise looks at them after the move) */
  tempting: string[];
}

/**
 * Every child turn: counts it; on the turns of the cadence (not in check, a real choice) asks 🚦 «Безопасно?» — said
 * aloud the first `habitSafeSayMax` times a game (and while a quiet game has lines left), later the icon alone.
 */
export function planHabitTurn(args: HabitTurnArgs, setup: HabitSetup, memory: HabitMemory, rng: Rng = Math.random): { ask: HabitAsk | null; memory: HabitMemory } {
  if (turnOf(args.fen) !== args.childColor) return { ask: null, memory };
  const turns = memory.turns + 1;
  let next: HabitMemory = { ...memory, turns };
  const due = turns - memory.lastAsk >= habitAskEvery(setup.games);
  if (!due || inCheck(args.fen) || legalUcis(args.fen).length < 2) return { ask: null, memory: next };
  next = { ...next, lastAsk: turns, asks: memory.asks + 1 };
  let event: CoachEvent | null = null;
  if (canSay(setup) && memory.safeSaid < habitSafeSayMax(setup.games)) {
    const said = line(`habit-safe-${args.ply}`, HABIT_POOL.safe, 'think', next, rng);
    if (said) {
      event = said.event;
      next = { ...said.memory, safeSaid: memory.safeSaid + 1 };
    }
  }
  return { ask: { event, tempting: temptingMoves(args.fen) }, memory: next };
}

// ───────────────────────── after the child's move: praise the process / «не спеши» ─────────────────────────

export interface HabitMoveArgs {
  fenBefore: string;
  uci: string;
  childColor: Color;
  ply: number;
  /** how long the child thought */
  spentMs: number;
  /** the move answered a 🚦 question */
  asked: boolean;
  /** the tempting losing moves of the position (from the ask; computed here when absent) */
  tempting?: readonly string[];
  /** help was shown for this move (a hint, an advice arrow, a quiet reveal — own.ts `unaidedMove` false): no praise */
  helped?: boolean;
  /** the judgement's loss of the move (win% points), when it was judged: a real loss is never praised */
  lossPct?: number;
}

export type HabitVerdict = 'praise' | 'slow';

/**
 * After the child's move: the process praised (a tempting losing move was there, he thought and played a safe one by
 * himself; ≤ `HABIT_PRAISE_MAX` a game, the asked turns or a move thought over; never a helped move, never one the
 * judgement shows losing) or one «не спеши» a game (a hasty or asked move that gives a piece ≥ 3 away). Nothing
 * without a voice for the habit.
 */
export function planHabitAfterMove(args: HabitMoveArgs, setup: HabitSetup, memory: HabitMemory, rng: Rng = Math.random): { kind: HabitVerdict | null; event: CoachEvent | null; memory: HabitMemory } {
  const none = { kind: null, event: null, memory };
  if (!canSay(setup) || turnOf(args.fenBefore) !== args.childColor) return none;
  const losing = args.lossPct !== undefined && args.lossPct >= HABIT_PRAISE_MAX_LOSS;
  const safe = !losing && habitSafe(args.fenBefore, args.uci);
  if (safe) {
    if (args.helped === true || memory.praises >= HABIT_PRAISE_MAX || (!args.asked && args.spentMs < HABIT_THINK_MS)) return none;
    const tempting = args.tempting ?? temptingMoves(args.fenBefore);
    if (tempting.length === 0 || tempting.includes(args.uci)) return none;
    const said = line(`habit-praise-${args.ply}`, HABIT_POOL.praise, 'cheer', memory, rng);
    return said ? { kind: 'praise', event: said.event, memory: { ...said.memory, praises: memory.praises + 1 } } : none;
  }
  if (memory.slowSaid || !(args.asked || args.spentMs < HABIT_HASTY_MS) || !gaveAPiece(args.fenBefore, args.uci)) return none;
  const said = line(`habit-slow-${args.ply}`, HABIT_POOL.slow, 'talk', memory, rng);
  return said ? { kind: 'slow', event: said.event, memory: { ...said.memory, slowSaid: true } } : none;
}
